import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Ship,
  Settings,
  Users,
  Database,
  FileText,
  LogOut,
  Menu,
  Activity,
  Wifi,
  HardDrive,
  ShieldCheck,
  Camera,
  ChevronRight
} from 'lucide-react';

type LayoutProps = {
  children: React.ReactNode;
  preview?: boolean;
};

const previewUser = {
  id: 'preview-user',
  email: 'preview@flconnect.local',
  name: 'Prévia FLConnect',
  role: 'GLOBAL_ADMIN' as const
};

const Layout: React.FC<LayoutProps> = ({ children, preview = false }) => {
  const { user, logout } = useAuth();
  const activeUser = preview ? previewUser : user;
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(() => window.innerWidth > 768);

  const handleLogout = () => {
    if (preview) {
      navigate('/login');
      return;
    }
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: 'Visão geral', icon: LayoutDashboard, section: 'OPERAÇÃO', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER', 'USER'] },
    { path: '/branches', label: 'Vessels', icon: Ship, section: 'OPERAÇÃO', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER'] },
    { path: '/hotspot-users', label: 'Usuários Hotspot', icon: Wifi, section: 'OPERAÇÃO', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER'] },
    { path: '/hotspot-profiles', label: 'Perfis Hotspot', icon: Settings, section: 'OPERAÇÃO', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER'] },
    { path: '/flcam', label: 'FLCAM', icon: Camera, section: 'OPERAÇÃO', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER'] },
    { path: '/data', label: 'Dados', icon: Database, section: 'ANÁLISE', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER', 'USER'] },
    { path: '/reports', label: 'Relatórios', icon: Activity, section: 'ANÁLISE', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER'] },
    { path: '/log', label: 'Histórico', icon: FileText, section: 'ANÁLISE', roles: ['GLOBAL_ADMIN', 'COMPANY_MANAGER'] },
    { path: '/companies', label: 'Empresas', icon: Building2, section: 'ADMINISTRAÇÃO', roles: ['GLOBAL_ADMIN'] },
    { path: '/requests', label: 'Coletas', icon: HardDrive, section: 'ADMINISTRAÇÃO', roles: ['GLOBAL_ADMIN'] },
    { path: '/users', label: 'Usuários', icon: Users, section: 'ADMINISTRAÇÃO', roles: ['GLOBAL_ADMIN'] },
    { path: '/audit', label: 'Auditoria', icon: ShieldCheck, section: 'ADMINISTRAÇÃO', roles: ['GLOBAL_ADMIN'] },
    { path: '/backup-restore', label: 'Backup e restore', icon: Database, section: 'ADMINISTRAÇÃO', roles: ['GLOBAL_ADMIN'] }
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(activeUser?.role || ''));
  const currentPage = preview
    ? 'Visão geral'
    : filteredMenuItems.find(item => item.path === location.pathname || location.pathname.startsWith(`${item.path}/`))?.label || 'Visão geral';
  const initials = activeUser?.name
    ? activeUser.name.split(' ').slice(0, 2).map(word => word[0]).join('').toUpperCase()
    : '?';

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  return (
    <div className={`app-shell${preview ? ' app-shell--preview' : ''}`}>
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <aside className={`sidebar ${sidebarOpen ? '' : 'sidebar--collapsed'}`}>
        <div className="sidebar__header">
          <div className="sidebar__brand-mark" aria-hidden="true"><Wifi size={19} strokeWidth={2.2} /></div>
          {sidebarOpen && (
            <div className="sidebar__brand-copy">
              <h1 className="sidebar__title">FLConnect</h1>
              <span>Network operations</span>
            </div>
          )}
        </div>

        <nav className="sidebar__nav" aria-label="Navegação principal">
          {['OPERAÇÃO', 'ANÁLISE', 'ADMINISTRAÇÃO'].map(section => {
            const items = filteredMenuItems.filter(item => item.section === section);
            if (items.length === 0) return null;
            return (
              <div className="sidebar__group" key={section}>
                {sidebarOpen && <p className="sidebar__section-label">{section}</p>}
                {items.map(item => {
                  const Icon = item.icon;
                  const isActive = preview
                    ? item.path === '/dashboard'
                    : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                  const blockedInPreview = preview && item.path !== '/dashboard';
                  return (
                    <Link
                      key={item.path}
                      to={preview ? '/mockup' : item.path}
                      className={`sidebar__link ${isActive ? 'sidebar__link--active' : ''}${blockedInPreview ? ' sidebar__link--preview-disabled' : ''}`}
                      title={!sidebarOpen ? item.label : blockedInPreview ? 'Disponível após autenticação' : undefined}
                      aria-current={isActive ? 'page' : undefined}
                      aria-disabled={blockedInPreview || undefined}
                      tabIndex={blockedInPreview ? -1 : undefined}
                      onClick={event => {
                        if (blockedInPreview) event.preventDefault();
                        closeSidebarOnMobile();
                      }}
                    >
                      <span className="sidebar__icon"><Icon size={17} strokeWidth={1.8} /></span>
                      {sidebarOpen && <span className="sidebar__label">{item.label}</span>}
                      {sidebarOpen && isActive && <span className="sidebar__active-mark" aria-hidden="true" />}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar__user-info" title={!sidebarOpen ? activeUser?.name : undefined}>
            <div className="sidebar__avatar">{initials}</div>
            {sidebarOpen && (
              <div className="sidebar__user-details">
                <p className="sidebar__user-name">{activeUser?.name}</p>
                <p className="sidebar__user-role">{preview ? 'Prévia do produto' : activeUser?.role.replace(/_/g, ' ')}</p>
              </div>
            )}
          </div>
          {!preview && (
            <button onClick={handleLogout} className="sidebar__logout">
              <LogOut size={16} />
              {sidebarOpen && <span>Sair</span>}
            </button>
          )}
        </div>
      </aside>

      <main className="app-main">
        <header className="topbar">
          <div className="topbar__left">
            <button className="topbar__hamburger" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Abrir ou recolher menu" aria-expanded={sidebarOpen}>
              <Menu size={19} />
            </button>
            <span className="topbar__workspace">FLConnect</span>
            <ChevronRight size={14} className="topbar__separator" aria-hidden="true" />
            <h2 className="topbar__title">{currentPage}</h2>
          </div>
          <div className="topbar__right">
            {preview && <span className="topbar__preview-tag">AMOSTRA · DADOS DEMONSTRATIVOS</span>}
            {!preview && <span className="topbar__user-name">{activeUser?.name}</span>}
            <div className="topbar__avatar" title={activeUser?.name}>{initials}</div>
          </div>
        </header>

        <div className="content-area">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
